import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { currentSession, getCurrentUserEmail, signOut } from "./auth/cognito";
import { TpeAdminPage } from "./pages/TpeAdminPage";
import { ConfigLeaderboardPage } from "./pages/ConfigLeaderboardPage";
import { AlphaLeaderboardPage } from "./pages/AlphaLeaderboardPage";
import { DailyDecisionsPage } from "./pages/DailyDecisionsPage";
import { TpeHistoryPage } from "./pages/TpeHistoryPage";
import { RunComparePage } from "./pages/RunComparePage";
import { RunDetailPage } from "./pages/RunDetailPage";
import { ConfigsPage } from "./pages/ConfigsPage";
import { LoginPage } from "./pages/LoginPage";
import { LeaderboardPage } from "./pages/LeaderboardPage";
import { ModelDetailPage } from "./pages/ModelDetailPage";
import { EnsemblePage } from "./pages/EnsemblePage";
import { ReplayPage } from "./pages/ReplayPage";
import { MonitorsPage } from "./pages/MonitorsPage";
import { ReportsPage } from "./pages/ReportsPage";
import { SymbolPage } from "./pages/SymbolPage";
import { TickersAdminPage } from "./pages/TickersAdminPage";
import { MarketStatusBanner } from "./components/MarketStatusBanner";

const TPE_ADMIN_EMAILS = new Set(["nick@kitmitto.com"]);

export function App() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  // Mobile-only: the hamburger toggles this open. Tap a link or the scrim
  // to close. On desktop the sidebar is always visible regardless of this.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    currentSession().then((s) => setAuthed(!!s));
  }, []);

  // Re-fetch the email whenever we transition to authed. Without this, signing
  // in via LoginPage flips `authed` but leaves `email` null, so admin-gated
  // nav links stay hidden until a full page reload.
  useEffect(() => {
    if (authed) getCurrentUserEmail().then(setEmail);
    else setEmail(null);
  }, [authed]);

  // Auto-close the mobile drawer on route change so a tapped link doesn't
  // leave the drawer covering the new page.
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  const isTpeAdmin = email !== null && TPE_ADMIN_EMAILS.has(email.toLowerCase());

  if (authed === null) return <div className="app">Loading…</div>;

  if (!authed) {
    return (
      <Routes>
        <Route path="*" element={<LoginPage onSignedIn={() => setAuthed(true)} />} />
      </Routes>
    );
  }

  return (
    <div className={`app${mobileNavOpen ? " nav-open" : ""}`}>
      <aside className="sidebar">
        <div className="sidebar-brand">
          <strong>Waltstreet</strong>
          <div className="sidebar-subtitle">Observability</div>
        </div>
        <nav className="nav">
          {/* NavLink auto-applies the "active" class when the route matches,
              which we style as a filled pill in styles.css so the current
              page is unambiguously visible. Sections split the model-training
              tools (Backtest Control) from the live-trading tools (Trade
              Control) so the mental model matches the org chart. */}
          <div className="nav-section-label">Backtest Control</div>
          <NavLink to="/" end>Leaderboard</NavLink>
          <NavLink to="/ensemble">Ensemble</NavLink>
          <NavLink to="/runs/history">Run history</NavLink>
          <NavLink to="/runs/alpha-leaderboard">Alpha leaderboard</NavLink>
          {isTpeAdmin && <NavLink to="/admin/tpe">TPE control</NavLink>}
          {isTpeAdmin && <NavLink to="/admin/configs/leaderboard">Config leaderboard</NavLink>}

          <div className="nav-section-label">Trade Control</div>
          <NavLink to="/decisions">Daily decisions</NavLink>
          <NavLink to="/reports">Reports</NavLink>
          <NavLink to="/monitors">Monitors</NavLink>
          {isTpeAdmin && <NavLink to="/admin/tickers">Tickers</NavLink>}
        </nav>
        <div className="sidebar-footer">
          {email && <div className="sidebar-email" title={email}>{email}</div>}
          <button
            className="signout"
            onClick={() => {
              signOut();
              setAuthed(false);
              navigate("/");
            }}
          >
            Sign out
          </button>
        </div>
      </aside>
      {/* Scrim sits between the page and the drawer on mobile so a tap
          outside the drawer closes it. Inert on desktop via CSS. */}
      <div
        className="nav-scrim"
        onClick={() => setMobileNavOpen(false)}
        aria-hidden="true"
      />
      <div className="main">
        <header className="mobile-header">
          <button
            className="hamburger"
            aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((v) => !v)}
          >
            <span /><span /><span />
          </button>
          <strong>Waltstreet</strong>
        </header>
        <MarketStatusBanner />
        <Routes>
          <Route path="/" element={<LeaderboardPage />} />
          <Route path="/models/:modelId" element={<ModelDetailPage />} />
          <Route path="/symbols/:symbol" element={<SymbolPage />} />
          <Route path="/ensemble" element={<EnsemblePage />} />
          <Route path="/admin/tpe" element={<TpeAdminPage />} />
          <Route path="/admin/tickers" element={<TickersAdminPage />} />
          <Route path="/admin/configs" element={<ConfigsPage />} />
          <Route path="/admin/configs/leaderboard" element={<ConfigLeaderboardPage />} />
          <Route path="/runs/alpha-leaderboard" element={<AlphaLeaderboardPage />} />
          <Route path="/decisions" element={<DailyDecisionsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/monitors" element={<MonitorsPage />} />
          <Route path="/runs/history" element={<TpeHistoryPage />} />
          <Route path="/runs/compare" element={<RunComparePage />} />
          <Route path="/runs/:runId" element={<RunDetailPage />} />
          <Route path="/replay/:symbol/:decisionTs" element={<ReplayPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
}
